import * as React from 'react';
import { BusinessMarker } from 'selectors/availabilitySearchSelectors';
import { Coordinates, core } from '@tock/coretypes';
import { GoogleMap } from '@react-google-maps/api';
import { Theme, css } from '@emotion/react';
import { getCityLatLong } from 'utils/locationUtils';
import { isEmpty, isEqual, isNumber, memoize } from 'es-toolkit/compat';
import { isTouchScreen } from 'utils/deviceUtils';
import { useIsVisglMapsEnabled } from 'featureFlags/useIsVisglMapsEnabled';
import { usePrevious } from '@tock/hooks';
import { useSearchHandlerContext } from 'components/search/SearchContext';
import ConsumerLocationDot from './ConsumerLocationDot';
import ConsumerMapNew from 'components/maps/ConsumerMapNew';
import CurrentLocationControl from 'components/profile/culinary-travel-log/CurrentLocationControl';
import Refresh from 'components/profile/culinary-travel-log/Refresh';
import ZoomControls from 'components/profile/culinary-travel-log/ZoomControls';

const locationControlStyles = css`
  padding: 1.8rem;
  padding-top: calc(1.8rem + env(safe-area-inset-top));
`;

// The minimum amount of pins we are going to contain in our zoom.
const MIN_PIN_COUNT = 8;
const MAP_ID = '52ad3d2f91d95eb5';

interface Props {
  children: React.ReactNode;
  hasControls?: boolean;
  isMapLoaded: boolean;
  latOffsetFactor?: number;
  loadError?: Error;
  locations: google.maps.LatLngLiteral[];
  locationsSortedGeographically?: boolean;
  markers?: BusinessMarker[];
  metroArea?: core.MetroArea;
  onMapClick(): void;
  selectedBusinessId?: number;
  showCurrentLocation?: boolean;
  gestureHandling?: google.maps.MapOptions['gestureHandling'];
  zoomControl?: google.maps.MapOptions['zoomControl'];
  zoom?: google.maps.MapOptions['zoom'];
  onSearch?(): void;
}

async function fitMapToLocations(
  map: google.maps.Map,
  locations: google.maps.LatLngLiteral[],
  metroArea?: core.MetroArea,
  locationsSortedGeographically?: boolean
) {
  let boundLocations = [...locations];
  if (locationsSortedGeographically && locations.length) {
    boundLocations = boundLocations.slice(0, MIN_PIN_COUNT);
  }

  const boundingBox = new google.maps.LatLngBounds();
  if (metroArea && boundLocations.length === 0) {
    try {
      const geocodedZip = await getCityLatLong(metroArea.name, metroArea.country);
      const viewport = geocodedZip.geometry.viewport;
      if (isNumber(viewport.getNorthEast().lat)) {
        boundingBox.extend(viewport.getNorthEast());
        boundingBox.extend(viewport.getSouthWest());
      } else {
        boundingBox.extend(
          new google.maps.LatLng(viewport.getNorthEast().lat(), viewport.getNorthEast().lng())
        );
        boundingBox.extend(
          new google.maps.LatLng(viewport.getSouthWest().lat(), viewport.getSouthWest().lng())
        );
      }
      map.fitBounds(boundingBox);
      return;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      // continue to the next attempt
    }
  }

  if (!metroArea && isEmpty(boundLocations)) {
    // Else manually center around the united states
    boundingBox.extend(new google.maps.LatLng(50, -125));
    boundingBox.extend(new google.maps.LatLng(30, -67.5));
    map.fitBounds(boundingBox);
    return;
  }

  const expandBoundingBox = (location: google.maps.LatLngLiteral) => {
    const { lat, lng } = location;
    // 0.01 delta in lat and lng is approx 1km hypotenuse
    // For each location cover atleast a 2 km bounding box.
    boundingBox.extend(new google.maps.LatLng(lat + 0.01, lng + 0.01));
    boundingBox.extend(new google.maps.LatLng(lat - 0.01, lng - 0.01));
  };

  if (boundLocations.length === 1) {
    expandBoundingBox(boundLocations[0]);
    map.fitBounds(boundingBox);
    return;
  }

  // Server already sorted for us in this case. No geometry needed!
  if (locationsSortedGeographically) {
    boundLocations.forEach(expandBoundingBox);
  } else {
    const centroid: google.maps.LatLngLiteral = { lat: 0, lng: 0 };

    boundLocations.forEach((location) => {
      centroid.lat += location.lat;
      centroid.lng += location.lng;
    });
    centroid.lat /= boundLocations.length;
    centroid.lng /= boundLocations.length;

    // turning lat and lng into a string key for memoizing purposes
    const latLngKey = ({ lat, lng }: google.maps.LatLngLiteral) => `${lat}-${lng}`;

    const distanceBetweenLatLngs = memoize(
      (a: google.maps.LatLngLiteral, b: google.maps.LatLngLiteral): number =>
        Math.sqrt(Math.pow(a.lat - b.lat, 2) + Math.pow(a.lng - b.lng, 2)),
      (a: google.maps.LatLngLiteral, b: google.maps.LatLngLiteral) =>
        `${latLngKey(a)}-${latLngKey(b)}`
    );

    boundLocations.sort((a, b) => {
      const distanceA = distanceBetweenLatLngs(a, centroid);
      const distanceB = distanceBetweenLatLngs(b, centroid);
      return distanceA - distanceB;
    });

    // Compute the 98th percentile entry and compute bounding box based items within that.
    for (let i = 0; i < Math.floor(boundLocations.length * 0.98); i++) {
      expandBoundingBox(boundLocations[i]);
    }
  }

  map.fitBounds(boundingBox);
}

const panMapToLocation = (map: google.maps.Map, location: google.maps.LatLngLiteral) => {
  map.panTo(location);
};

const findMarkerForBusiness = (markers: BusinessMarker[], selectedBusinessId: number) => {
  return markers.find((marker) => marker.id === selectedBusinessId);
};

/**
 * Slimmed down Google Map wrapper that will center based on provided lat/lng and
 * apply custom controls.
 */
function ConsumerMap({
  children,
  gestureHandling,
  hasControls = true,
  isMapLoaded,
  latOffsetFactor = 0,
  loadError,
  locations,
  locationsSortedGeographically,
  markers,
  metroArea,
  onMapClick,
  selectedBusinessId,
  showCurrentLocation = false,
  zoom,
  zoomControl,
}: Props) {
  const visglEnabled = useIsVisglMapsEnabled();
  const mapRef = React.useRef<google.maps.Map>();
  const [initialZoom, setInitialZoom] = React.useState<number>();
  const [currentLocation, setCurrentLocation] = React.useState<Coordinates | undefined>();
  const [showRedoSearch, setShowRedoSearch] = React.useState(false);
  const isTouch = isTouchScreen();
  const searchHandlerContext = useSearchHandlerContext();

  const updateSearchUrl = searchHandlerContext?.updateSearchUrl;

  React.useEffect(() => {
    if (mapRef.current) {
      const currentZoom = mapRef.current.getZoom();
      // if the current zoom is initial, and a custom zoom was provided
      if (currentZoom === initialZoom && zoom && zoom !== initialZoom) {
        mapRef.current.setZoom(zoom);
      }
    }
    if (markers && markers.length > 0 && mapRef.current && selectedBusinessId) {
      const newMarkerSelected = findMarkerForBusiness(markers, selectedBusinessId);
      if (newMarkerSelected) {
        const bounds = mapRef.current.getBounds();
        // only pan the map if the pin is out of the current view
        if (bounds && !bounds.contains(newMarkerSelected.location)) {
          const neLat = bounds?.getNorthEast().lat();
          const swLat = bounds?.getSouthWest().lat();
          const offset = (neLat - swLat) * latOffsetFactor;
          panMapToLocation(mapRef.current, {
            ...newMarkerSelected.location,
            lat: newMarkerSelected.location.lat - offset,
          });
        }
      }
    }
  }, [selectedBusinessId, latOffsetFactor, markers, zoom, initialZoom]);

  // Weirdly, replacing this with a literal causes strange map behavior on pin selection
  const [center] = React.useState({ lat: 44.076613, lng: -98.362239833 });
  const [loadingMetro, setLoadingMetro] = React.useState<boolean>(false);
  const prevLocations = usePrevious(locations);
  const prevMetroArea = usePrevious(metroArea);

  React.useEffect(() => {
    async function refitMap() {
      if (mapRef.current) {
        await fitMapToLocations(
          mapRef.current,
          locations,
          metroArea,
          locationsSortedGeographically
        );
      }
    }
    if (metroArea !== prevMetroArea) {
      setLoadingMetro(true);
    }
    // only respond to locations changes when we're changing metros
    if (metroArea && loadingMetro && !isEqual(locations, prevLocations)) {
      refitMap().then(() => {
        setShowRedoSearch(false);
        setLoadingMetro(false);
      });
    }
  }, [
    isMapLoaded,
    metroArea,
    prevMetroArea,
    locations,
    locationsSortedGeographically,
    loadingMetro,
    prevLocations,
  ]);

  const onLocation = (location: Coordinates) => {
    setCurrentLocation(location);
  };

  const onRefreshWithBounds = () => {
    const bounds = mapRef.current?.getBounds();
    if (!bounds || bounds.isEmpty()) {
      return;
    }

    const { lat: topRightLat, lng: topRightLon } = bounds.getNorthEast();
    const { lat: bottomLeftLat, lng: bottomLeftLon } = bounds.getSouthWest();
    updateSearchUrl &&
      updateSearchUrl({
        topLeftLat: topRightLat().toString(),
        topLeftLon: bottomLeftLon().toString(),
        bottomRightLat: bottomLeftLat().toString(),
        bottomRightLon: topRightLon().toString(),
      });
    setShowRedoSearch(false);
  };

  // Current location control button
  const currentLocationControlContent = mapRef.current ? (
    <CurrentLocationControl
      css={locationControlStyles}
      currentLocation={currentLocation}
      latOffsetFactor={latOffsetFactor}
      map={mapRef.current}
      onLocation={onLocation}
    />
  ) : null;

  // search this area button
  const refreshSearch =
    mapRef.current && updateSearchUrl ? <Refresh onClick={onRefreshWithBounds} /> : null;

  // Zoom control buttons
  const zoomControlsContent = mapRef.current ? <ZoomControls map={mapRef.current} /> : null;

  // We want to show zoom controls whenever we're not using a touch device
  const customControlsContainer = (
    <div
      css={({ breakpoints }: Theme) => css`
        position: absolute;
        left: 0;

        ${breakpoints.up('sm')} {
          left: initial;
          right: 0;
        }

        & svg {
          font-size: 1.6rem;
        }
      `}
    >
      {hasControls || !isTouch ? zoomControlsContent : null}
      {showCurrentLocation ? currentLocationControlContent : null}
    </div>
  );

  const centerControlsContainer = (
    <div
      css={({ breakpoints }: Theme) => css`
        position: absolute;
        transform: translate(50%, 0);
        left: initial;
        right: 50%;

        & svg {
          font-size: 1.6rem;
        }
      `}
    >
      {showRedoSearch ? refreshSearch : null}
    </div>
  );

  const onMapLoad = (loadedMap: google.maps.Map | undefined) => {
    if (!loadedMap) {
      return;
    }
    fitMapToLocations(loadedMap, locations, metroArea, locationsSortedGeographically)
      .then(() => {
        if (markers && markers.length > 0 && selectedBusinessId) {
          const newMarkerSelected = findMarkerForBusiness(markers, selectedBusinessId);
          if (newMarkerSelected) {
            panMapToLocation(loadedMap, newMarkerSelected.location);
          }
        }
      })
      .then(() => {
        setShowRedoSearch(false);
      });
    loadedMap.addListener('bounds_changed', () => {
      setShowRedoSearch(true);
    });
    setShowRedoSearch(false);
    mapRef.current = loadedMap;
  };

  const onZoomChanged = () => {
    if (!mapRef.current) {
      return;
    }
    const currentZoom = mapRef.current?.getZoom();
    if (!initialZoom) {
      setInitialZoom(currentZoom);
    }
  };

  // TODO(visgl-burn-in): remove this flag fork and the legacy `<GoogleMap>` branch
  // below (keep ConsumerMapNew) once CONSUMER_ENABLE_GOOGLE_MAPS_API_UPDATE is fully
  // rolled out.
  if (visglEnabled) {
    return (
      <ConsumerMapNew
        hasControls={hasControls}
        isMapLoaded={isMapLoaded}
        latOffsetFactor={latOffsetFactor}
        loadError={loadError}
        locations={locations}
        locationsSortedGeographically={locationsSortedGeographically}
        markers={markers}
        metroArea={metroArea}
        onMapClick={onMapClick}
        selectedBusinessId={selectedBusinessId}
        showCurrentLocation={showCurrentLocation}
        gestureHandling={gestureHandling}
        zoomControl={zoomControl}
        zoom={zoom}
      >
        {children}
      </ConsumerMapNew>
    );
  }

  if (!isMapLoaded) {
    return <div />;
  }

  if (loadError) {
    return <div>{loadError}</div>;
  }

  const options = {
    clickableIcons: false,
    disableDefaultUI: true,
    scrollwheel: false,
    keyboardShortcuts: false,
    fullscreenControl: false,
    gestureHandling,
    zoomControl,
    mapId: MAP_ID,
  };

  // TODO ARIA ROLE
  return (
    <GoogleMap
      onLoad={onMapLoad}
      onClick={onMapClick}
      options={options}
      onZoomChanged={onZoomChanged}
      zoom={12}
      center={center}
      mapContainerStyle={{ width: '100%', height: '100%' }}
    >
      {centerControlsContainer}
      {customControlsContainer}
      {children}
      {currentLocation && (
        <ConsumerLocationDot
          position={{ lat: currentLocation.latitude, lng: currentLocation.longitude }}
        />
      )}
    </GoogleMap>
  );
}

export default ConsumerMap;
